import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import useOrdersResource from "../../../resources/useOrdersResource";
import { toast } from "react-toastify";
import type { StripeCheckoutRouterState } from "../../../resources/types/packagesTypes";
import { config } from "../../../utils/config";

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.
// This is your test secret API key.
const stripePromise = loadStripe(config.stripePublicKey);

const CheckoutForm = () => {
  const { createOrder } = useOrdersResource();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPackage = (location.state as StripeCheckoutRouterState)
    ?.selectedPackage;

  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const initializationRef = useRef(false);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Initialize order on mount
  useEffect(() => {
    const initializeOrder = async () => {
      // Prevent multiple initializations
      if (initializationRef.current) return;
      initializationRef.current = true;

      try {
        if (!selectedPackage?.id) {
          setError("No package selected. Please select a package first.");
          setTimeout(() => {
            if (mountedRef.current) {
              navigate(-1);
            }
          }, 3000);
          return;
        }

        const response = await createOrder({ package_id: selectedPackage.id });
        const paymentSessionId = response?.order?.payment_session_id;

        if (!paymentSessionId) {
          throw new Error("Payment session ID not found in response");
        }

        const orderId = response?.order.id;
        if (!orderId) {
          throw new Error("Order ID not found in response");
        }

        localStorage.setItem("order_id", orderId);

        if (mountedRef.current) {
          setClientSecret(paymentSessionId);
        }
      } catch (error) {
        console.error("Error creating order:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create order";

        if (mountedRef.current) {
          setError(errorMessage);
          toast.error("Failed to create order. Please try again.");
        }
      } finally {
        if (mountedRef.current) {
          setIsInitializing(false);
        }
      }
    };

    initializeOrder();
  }, [selectedPackage, createOrder, navigate]);

  // Show initializing state
  if (isInitializing) {
    return (
      <div
        id="checkout"
        className="flex items-center justify-center min-h-[400px]"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sangati-green mx-auto mb-4"></div>
          <p className="text-gray-600">Setting up payment...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        id="checkout"
        className="flex items-center justify-center min-h-[400px]"
      >
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Payment Setup Error
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-sangati-green text-white rounded hover:bg-green-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Show checkout when ready
  if (selectedPackage && clientSecret) {
    return (
      <EmbeddedCheckoutProvider
        key={`stripe-checkout-${selectedPackage.id}-${clientSecret.slice(-8)}`}
        stripe={stripePromise}
        options={{ clientSecret }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    );
  }

  return null;
};

export default CheckoutForm;
